import { useState } from 'react';
import { Comment } from '../comments/Comment';
import { CommentForm } from '../comments/CommentForm';
import { UserLink } from '../shared/UserLink';
import type { Comment as CommentType } from '../comments/commentTypes';
import { IssueStatus, type IssueStatusId, type Issue as IssueType } from './issueTypes';
import { useAuth } from '../shared/AuthContext';
import { formatDate } from '../shared/dates';

interface IssueProps
{
	issue: IssueType;
	onDelete: (id: number) => void;
	onReply?: (issueId: number, text: string, position?: string) => Promise<void>; // Optional - not needed on list page
	onCloseReopen: (issueId: number, newStatus: IssueStatusId) => Promise<void>;
	// Optional props for list context
	showReplyForm?: boolean; // Default: true (show reply form)
	comments?: CommentType[]; // If provided, use these instead of issue.comments
	author?: { name: string }; // If provided, use this instead of issue.user_display_name
	isDraggingEnabled?: boolean; // Default: true (enable dragging on play page)
}

export function Issue({
	issue,
	onDelete,
	onReply,
	onCloseReopen,
	showReplyForm = true, // Default to showing reply form
	comments, // Optional override
	author, // Optional override
	isDraggingEnabled = true // Default to enabling dragging
}: IssueProps)
{
	const { userId, isAdmin } = useAuth();
	const [reply, setReply] = useState({ show: false, submitting: false });
	const isOpen = issue.tsumego_issue_status_id === IssueStatus.OPEN;
	const canCloseReopen = isAdmin || userId === issue.user_id;

	// Use provided comments/author or fall back to issue data
	const displayComments = comments ?? issue.comments ?? [];
	const authorName = author?.name ?? issue.user_display_name ?? '[account deleted]';

	const handleCloseReopen = async () =>
	{
		setReply(r => ({ ...r, submitting: true }));
		try
		{
			await onCloseReopen(issue.id, isOpen ? IssueStatus.CLOSED : IssueStatus.OPEN);
		}
		finally
		{
			setReply(r => ({ ...r, submitting: false }));
		}
	};

	const handleSubmitReply = async (text: string, position?: string) =>
	{
		if (!onReply)
			return;
		setReply(r => ({ ...r, submitting: true }));
		try
		{
			await onReply(issue.id, text, position);
			setReply({ show: false, submitting: false });
		}
		finally
		{
			setReply(r => ({ ...r, submitting: false }));
		}
	};

	return (
		<div className={`tsumego-issue tsumego-issue--${isOpen ? 'opened' : 'closed'}`} data-issue-id={issue.id}>
			<div className="tsumego-issue__header">
				<span className="tsumego-issue__title">Issue #{issue.id}</span>
				<span className={`tsumego-issue__badge status--${isOpen ? 'opened' : 'closed'}`}>
					{isOpen ? '🔴' : '✅'} {isOpen ? 'Opened' : 'Closed'}
				</span>
				<span className="tsumego-issue__meta">
					by{' '}
					<UserLink
						userId={issue.user_id}
						name={authorName}
						avatarUrl={issue.user_avatar_url}
						rating={issue.user_rating}
					/>{' '}
					•
					<time className="tsumego-issue__date" dateTime={issue.created}>
						{formatDate(issue.created, 'date')}
					</time>
				</span>
				{canCloseReopen && (
					<span className="tsumego-issue__actions">
						{isOpen ? (
							<button
								type="button"
								className="btn btn--success btn--small"
								onClick={handleCloseReopen}
								disabled={reply.submitting}
							>
								✓ Close Issue
							</button>
						) : (
							<button
								type="button"
								className="btn btn--warning btn--small"
								onClick={handleCloseReopen}
								disabled={reply.submitting}
							>
								↩ Reopen
							</button>
						)}
					</span>
				)}
			</div>

			<div className="tsumego-dnd__issue-dropzone" data-issue-id={issue.id}>
				{displayComments.map(c => (
					<Comment
						key={c.id}
						comment={c}
						onDelete={onDelete}
						showIssueContext={false}
						issueStatus={issue.tsumego_issue_status_id as IssueStatusId}
						isDraggingEnabled={isDraggingEnabled}
					/>
				))}
			</div>

			{showReplyForm && userId && (
				<div className="tsumego-issue__reply-toggle">
					<button
						type="button"
						className="tsumego-issue__reply-btn"
						onClick={() => setReply(r => ({ ...r, show: !r.show }))}
					>
						💬 Reply to this issue
					</button>
				</div>
			)}

			{showReplyForm && reply.show && userId && (
				<div className="tsumego-issue__reply-form">
					<CommentForm
						onSubmit={handleSubmitReply}
						isSubmitting={reply.submitting}
						showReportAsIssue={false}
						submitButtonText="Post Reply"
						placeholder="Write a reply..."
					/>
				</div>
			)}
		</div>
	);
}
